import { ButtonVariant } from "@galaxy-io/dls/buttons/Button";
import { InputSize } from "@galaxy-io/dls/inputs/Input";
import { workflows } from "@gtm/contracts/workflows/catalog";
import { XIcon } from "@phosphor-icons/react";
import { startTransition, useOptimistic } from "react";
import { useRouter } from "@/app/navigation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CompactSelect, SearchForm } from "@/components/workspace/people-styles";
import { SelectField } from "@/components/workspace/select-field";
import { SourceIcon } from "@/components/workspace/source-icon";
import { RUN_FILTER_KEYS, TIME_RANGE_OPTIONS } from "@/lib/filters";
import { sourceLabel } from "@/lib/source-label";

// Execution statuses as Temporal reports them.
export const RUN_STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "running", label: "Running" },
  { value: "completed", label: "Completed" },
  { value: "failed", label: "Failed" },
  { value: "cancelled", label: "Cancelled" },
  { value: "terminated", label: "Terminated" },
  { value: "timed_out", label: "Timed out" },
];
export function RunFilters({
  query,
  repositories,
}: {
  query: Record<string, string | undefined>;
  repositories: Array<{ id: string; name: string; provider: string }>;
}) {
  const router = useRouter();
  const [filters, updateFilters] = useOptimistic(
    query,
    (current, values: Record<string, string>) => ({ ...current, ...values }),
  );
  function apply(values: Record<string, string>) {
    const next = new URLSearchParams({ view: "history", kind: "collection" });
    for (const key of RUN_FILTER_KEYS) {
      const value = key in values ? values[key] : filters[key];
      if (value) next.set(key, value);
    }
    startTransition(() => {
      updateFilters(values);
      router.push(`/workflows?${next}`);
    });
  }
  const active = RUN_FILTER_KEYS.some((key) => filters[key]);
  return (
    <>
      <SearchForm
        onSubmit={(event) => {
          event.preventDefault();
          apply({
            q: String(new FormData(event.currentTarget).get("q") || ""),
          });
        }}
      >
        <Input
          key={query.q || ""}
          name="q"
          aria-label="Search runs"
          placeholder="Search runs, sources, errors…"
          defaultValue={query.q || ""}
          type="search"
          size={InputSize.SMALL}
          label="Search runs"
        />
      </SearchForm>
      <CompactSelect $width={200}>
        <SelectField
          compact
          label="Workflow"
          value={filters.workflow || ""}
          onChange={(workflow) => apply({ workflow })}
          options={[
            { value: "", label: "All workflows" },
            ...workflows.map((w) => ({ value: w.key, label: w.name })),
          ]}
        />
      </CompactSelect>
      <CompactSelect $width={150}>
        <SelectField
          compact
          label="Run status"
          value={filters.status || ""}
          onChange={(status) => apply({ status })}
          options={RUN_STATUS_OPTIONS}
        />
      </CompactSelect>
      <CompactSelect $width={140}>
        <SelectField
          compact
          label="Trigger"
          value={filters.trigger || ""}
          onChange={(trigger) => apply({ trigger })}
          options={[
            { value: "", label: "Any trigger" },
            { value: "manual", label: "Manual" },
            { value: "schedule", label: "Scheduled" },
          ]}
        />
      </CompactSelect>
      <CompactSelect $width={200}>
        <SelectField
          compact
          search
          label="Source"
          value={filters.repository || ""}
          onChange={(repository) => apply({ repository })}
          options={[
            { value: "", label: "All sources" },
            ...repositories.map((r) => ({
              value: r.id,
              label: sourceLabel(r.provider, r.name),
              icon: <SourceIcon source={r.provider} size={16} />,
            })),
          ]}
        />
      </CompactSelect>
      <CompactSelect $width={130}>
        <SelectField
          compact
          label="Time range"
          value={filters.days || ""}
          onChange={(days) => apply({ days })}
          options={TIME_RANGE_OPTIONS}
        />
      </CompactSelect>
      {active && (
        <Button
          label="Clear"
          ariaLabel="Clear run filters"
          icon={XIcon}
          variant={ButtonVariant.TERTIARY}
          onClick={() => apply(Object.fromEntries(RUN_FILTER_KEYS.map((key) => [key, ""])))}
          tooltip="Clear run filters"
        />
      )}
    </>
  );
}
